// src/features/help/HelpCenter.tsx
// In-app help. A searchable help dialog the user opens from anywhere (for
// example, a "?" button in the header). Topics are built in as data; search
// filters by title and body. Matches the app's shadcn dialog idiom; no new
// dependencies. Audience-aware: pass persona="manager" | "tenant" to show the
// relevant topics (defaults to manager).

import { useMemo, useState } from "react";
import { Search, HelpCircle, ChevronDown } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type HelpPersona = "manager" | "tenant";

type HelpTopic = {
  id: string;
  title: string;
  body: string;
  personas: HelpPersona[];
  keywords?: string;
};

const TOPICS: HelpTopic[] = [
  // Manager
  {
    id: "add-property",
    title: "Add a property",
    body:
      "Go to Properties and add a new property, then add its units from the Units section. " +
      "A property holds one or more rentable units.",
    personas: ["manager"],
    keywords: "building create new property unit",
  },
  {
    id: "add-tenant",
    title: "Add a tenant and invite them to the portal",
    body:
      "Go to Tenants and create the tenant record. To give them portal access, open the tenant " +
      "and click \"Invite to portal\". They need a real email on file to be invited.",
    personas: ["manager"],
    keywords: "resident renter invite portal access email",
  },
  {
    id: "create-lease",
    title: "Create a lease",
    body:
      "Go to Leases and create one, linking a tenant to a unit with a term, rent, and deposits. " +
      "You can also upload a lease PDF and let MapleKey abstract the details and match them to " +
      "existing tenants and units for your review.",
    personas: ["manager"],
    keywords: "lease pdf upload abstract term rent deposit",
  },
  {
    id: "generate-charges",
    title: "Charge rent for the month",
    body:
      "Go to Finance > Rent, choose the month, and click \"Generate charges\". This creates one " +
      "charge per active lease. Running it again will not create duplicates.",
    personas: ["manager"],
    keywords: "rent charge generate month billing",
  },
  {
    id: "record-payment",
    title: "Record a rent payment",
    body:
      "On Finance > Rent, find the lease and click \"Record payment\". Partial payments are " +
      "allowed; the outstanding balance updates automatically.",
    personas: ["manager"],
    keywords: "payment partial outstanding paid",
  },
  {
    id: "see-arrears",
    title: "See who is behind on rent",
    body:
      "Finance > Rent shows each lease as Unpaid, Partial, Paid, or Credit, and a total " +
      "outstanding. Arrears is the charge minus payments received.",
    personas: ["manager"],
    keywords: "arrears overdue outstanding behind unpaid",
  },
  {
    id: "send-notice",
    title: "Send a notice to one tenant",
    body:
      "Open the tenant's page and click \"Send notice\". Pick a notice type, edit the pre-filled " +
      "template, choose whether to also send by email, and send. The email greets the tenant by " +
      "name automatically.",
    personas: ["manager"],
    keywords: "notice message email template send tenant",
  },
  {
    id: "arrears-campaign",
    title: "Remind everyone in arrears at once",
    body:
      "On Finance > Rent, click \"Remind N in arrears\". Review how many tenants will receive it " +
      "(and how many have no email), edit the message, and decide on email (off by default). " +
      "Click send, then confirm on the banner that states the exact consequence. The confirmation " +
      "step prevents accidental bulk emails.",
    personas: ["manager"],
    keywords: "arrears campaign bulk reminder overdue confirm email",
  },
  {
    id: "owner-statement",
    title: "View an owner statement",
    body:
      "Go to Owners and open an owner. The statement shows collected rent, expenses, the " +
      "management fee (a percentage of collected rent), and the resulting distribution.",
    personas: ["manager"],
    keywords: "owner statement distribution management fee",
  },
  {
    id: "tax-export",
    title: "Export taxes (T776)",
    body:
      "Go to Reports > Tax. Choose an owner and year, pick cash or accrual basis, and export to " +
      "CSV or PDF. The mapping from transactions to T776 lines is explicit and auditable.",
    personas: ["manager"],
    keywords: "tax t776 export cash accrual csv pdf canada",
  },
  {
    id: "ask-brain",
    title: "Ask the Property Brain about your portfolio",
    body:
      "Open Brain and ask in plain language, for example \"how many vacancies\", \"who is in " +
      "arrears\", or \"what maintenance is open\". It answers from your live data and cites its " +
      "sources. For exact record details, open the record itself.",
    personas: ["manager"],
    keywords: "brain ai assistant question portfolio vacancy arrears maintenance",
  },
  // Tenant
  {
    id: "report-issue",
    title: "Report a maintenance problem",
    body:
      "On your home screen, tap \"Report an issue\", describe the problem, and add photos (up to " +
      "four) if they help. You can track the status of your requests afterward.",
    personas: ["tenant"],
    keywords: "maintenance repair issue report photo request",
  },
  {
    id: "read-messages",
    title: "Read messages from your manager",
    body:
      "Tap \"Messages\" on your home screen. Unread notices are marked \"New\"; tap one to read " +
      "it, which marks it as read.",
    personas: ["tenant"],
    keywords: "messages notice manager unread read",
  },
  {
    id: "my-lease",
    title: "See my rent and lease details",
    body:
      "Your home screen shows your unit and address, your lease start and end dates and status, " +
      "and your costs: rent, rent due day, deposits, and any late-fee terms.",
    personas: ["tenant"],
    keywords: "rent lease unit cost deposit due date",
  },
  {
    id: "no-invite",
    title: "I did not get my portal invite",
    body:
      "Contact your property manager and confirm they have your correct email address on file. " +
      "They can re-send the invitation.",
    personas: ["tenant"],
    keywords: "invite portal access email missing signup",
  },
];

function HelpItem({ topic }: { topic: HelpTopic }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-3 text-left min-h-[44px]"
      >
        <span className="text-sm font-medium">{topic.title}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-border px-3 pb-3 pt-2">
          <p className="text-sm text-muted-foreground">{topic.body}</p>
        </div>
      )}
    </div>
  );
}

export function HelpCenter({
  open,
  onOpenChange,
  persona = "manager",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  persona?: HelpPersona;
}) {
  const [query, setQuery] = useState("");

  const topics = useMemo(() => {
    const forPersona = TOPICS.filter((t) => t.personas.includes(persona));
    const q = query.trim().toLowerCase();
    if (!q) return forPersona;
    return forPersona.filter((t) =>
      (t.title + " " + t.body + " " + (t.keywords ?? "")).toLowerCase().includes(q),
    );
  }, [query, persona]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5" />
            Help
          </DialogTitle>
          <DialogDescription>
            Search for a task, or browse the topics below.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring min-h-[44px]"
          />
        </div>

        <div className="space-y-2">
          {topics.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No help topics match that search. Try a different term, or contact your administrator.
            </p>
          ) : (
            topics.map((t) => <HelpItem key={t.id} topic={t} />)
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
